import { createSlice } from '@reduxjs/toolkit';
import type {PayloadAction} from "@reduxjs/toolkit";
import { CustomEventTypes } from '../../globals';

export interface IEditorData
{
	updater: number,
	timelineId?: string,
	visibleStartFrame: number,
	visibleEndFrame: number,
	totalTime: number,
	frameInterval: number,
	lodLevel: number,
	currentFrame: number,
	isPlaying: boolean,
	isRecording: boolean,
	cachedFrame: number,
	selectedElementIDs: string[],
	isElementInspected: boolean,
	clipboard: any[],
	globalHeaderSize: number,
	scrollOffset: number,
	trackboxMaxScroll: number,
	trackHeaderHeight: number,
	spaceBetweenTracks: number,
	spaceBetweenObjects: number,
	spaceBetweenChannels: number,
}

export interface ITimelineState {
	elementData: Record<string, any>,
	editorData: IEditorData,
	selectedElementIDs: string[],
	isElementInspected: boolean,
	clipboard: any[],
	globalHeaderSize: number,
	spaceBetweenObjects: number,
	spaceBetweenChannels: number,
}

const initialState: ITimelineState = {
	elementData: {},
	editorData: {
		updater: 0,
		visibleStartFrame: 0,
		visibleEndFrame: 330,
		totalTime: 11,
		frameInterval: 1,
		lodLevel: 1,
		currentFrame: 0,
		isPlaying: false,
		isRecording: false,
		cachedFrame: 0,
		selectedElementIDs: [],
		isElementInspected: false,
		clipboard: [],
		globalHeaderSize: 28,
		scrollOffset: 0,
		trackboxMaxScroll: 0,
		trackHeaderHeight: 20,
		spaceBetweenTracks: 0,
		spaceBetweenObjects: 1,
		spaceBetweenChannels: 1,
	},
	selectedElementIDs: [],
	isElementInspected: false,
	clipboard: [],
	globalHeaderSize: 28,
	spaceBetweenObjects: 1,
	spaceBetweenChannels: 1,
};

const TimelineSlice = createSlice({
	name: "timeline",
	initialState,
	reducers: {
		setElementData(state, action: PayloadAction<{ dataId: string, data: any }>)
		{
			const { dataId, data } = action.payload;
			state.elementData[dataId] = { ...data };
		},
		reparentElementData(state, action: PayloadAction<{ dataId: string, newDataId: string }>)
		{
			const { dataId, newDataId } = action.payload;
			state.elementData[newDataId] = { ...state.elementData[dataId] };
			delete state.elementData[dataId];
		},
		setElementProperty(state, action: PayloadAction<{ elementId: string, propertyName: string, propertyValue: any }>)
		{
			const { elementId, propertyName, propertyValue } = action.payload;

			if (!state.elementData[elementId])
			{
				state.elementData[elementId] = {};
			}

			state.elementData[elementId][propertyName] = propertyValue;
		},
		updateEditorUI(state, action: PayloadAction<undefined>)
		{
			state.editorData.updater++;
		},
		updateEditorState(state, action: PayloadAction<Partial<IEditorData>>)
		{
			if (state.editorData.timelineId !== action.payload.timelineId)
			{
				state.editorData.isElementInspected = false;
			}
			state.editorData = { ...state.editorData, ...action.payload };
		},
		setPlaybackState(state, action: PayloadAction<boolean>)
		{
			state.editorData.isPlaying = action.payload;
			document.dispatchEvent(new CustomEvent(state.editorData.isPlaying ? CustomEventTypes.timelinePlaybackStarted : CustomEventTypes.timelinePlaybackStoped));
		},
		setRecordState(state, action: PayloadAction<boolean>)
		{
			state.editorData.isRecording = action.payload;
		},
		setCurrentFrame(state, action: PayloadAction<number>)
		{
			//WC20-1491 : interactively scroll backwards to reveal earlier time segment
			const shiftLeft = state.editorData.visibleStartFrame - action.payload;
			const shiftRight = action.payload - state.editorData.visibleEndFrame;

			if (shiftLeft > 0)
			{
				state.editorData.visibleStartFrame = state.editorData.visibleStartFrame - shiftLeft;
				state.editorData.visibleEndFrame = state.editorData.visibleEndFrame - shiftLeft;
			}
			if (shiftRight > 0)
			{
				state.editorData.visibleStartFrame = state.editorData.visibleStartFrame + shiftRight;
				state.editorData.visibleEndFrame = state.editorData.visibleEndFrame + shiftRight;
			}

			state.editorData.currentFrame = action.payload;
		},
		setCachedFrame(state, action: PayloadAction<number>)
		{
			state.editorData.cachedFrame = action.payload;
		},
		setInspectedElement(state, action: PayloadAction<boolean>)
		{
			state.editorData.isElementInspected = action.payload;
		},
		setFrameInterval(state, action: PayloadAction<number>)
		{
			state.editorData.frameInterval = action.payload;
		},
		setStartFrame(state, action: PayloadAction<number>)
		{
			state.editorData.visibleStartFrame = action.payload;
		},
		setFrameRange(state, action: PayloadAction<{ start: number, end: number }>)
		{
			state.editorData.visibleStartFrame = action.payload.start;
			state.editorData.visibleEndFrame = action.payload.end;
		},
		setEndFrame(state, action: PayloadAction<number>)
		{
			state.editorData.visibleEndFrame = action.payload;
		},
		setClipboard(state, action: PayloadAction<Array<any>>)
		{
			state.editorData.clipboard = [...action.payload];
		},
		setScrollOffset(state, action: PayloadAction<number>)
		{
			state.editorData.scrollOffset = action.payload;
		},
		setTrackboxMaxScroll(state, action: PayloadAction<number>)
		{
			state.editorData.trackboxMaxScroll = action.payload;
		},
		setLodLevel(state, action: PayloadAction<number>)
		{
			state.editorData.lodLevel = action.payload;
		},
		setTotalTime(state, action: PayloadAction<number>)
		{
			state.editorData.totalTime = action.payload;
		},
		setSelectedElements(state, action: PayloadAction<Array<string>>)
		{
			state.editorData.selectedElementIDs = [...action.payload];
			if (!state.editorData.selectedElementIDs.length)
			{
				TimelineSlice.caseReducers.setInspectedElement(state, { payload: false, type: "" });
			}
		},
	},
});

export default TimelineSlice;
